import api from "@/app/api";
import type { User } from "@/app/services/generated/userService";
import {
  assemblyQcMeasurementService,
  AssemblyQcMeasurement,
  AssemblyQcMeasurementLine,
} from "@/app/services/generated/assemblyqcmeasurementService";
import Guid from "@/app/types/common/guid";
import type { Ref } from "vue";
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import type { Vessel } from "@/app/services/generated/vesselv2Service";
import {
  makeDeepRef,
  makeRef,
  withLoading,
  wrapPromiseWithErrorHandling,
} from "@/components/composables/componentTools";
import { useAssemblyQcTests } from "@/components/pages/production/qctests/composables/assemblyQcTests";
import { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import type {
  ProductionQcTestMetricDefinition,
  ProductionQcTestMetricExpectation,
} from "@/components/pages/production/qctests/composables/qcTests";
import { Instant } from "@js-joda/core";
import userStore from "@/store/modules/userStore";
import { userService } from "@/app/services/extensions/userServiceExtended";
import { vesselService } from "@/app/services/extensions/vesselv2ServiceExtended";
import type { Maybe } from "@/components/utils/typeHelpers";
import { isNotNullOrUndefined } from "@/app/apiutils";

export interface ProductionQcMeasurementResult {
  readonly value: number | null;
  readonly valueString: string | null;
  readonly valueBoolean: boolean | null;
}

export interface ProductionQcMeasurementLine extends ProductionQcMeasurementResult {
  readonly assemblyQcTestMetricLineId: Guid;
  readonly metricDefinition: ProductionQcTestMetricDefinition;
  readonly expectations: ProductionQcTestMetricExpectation;
}

export interface ProductionQcMeasurementHeader {
  readonly assemblyQcTestId: Guid;
  readonly name: string;
  readonly sequence: number;
  readonly measurementLines: ProductionQcMeasurementLine[];
}

/**
 * Composable for capturing measurements against an assembly.
 */
export function useCaptureAssemblyQcMeasurements(
  assemblyId: Ref<Guid | undefined>,
  initialVesselId: Ref<Guid | undefined>,
  assemblyQcMeasurementToEdit: Ref<AssemblyQcMeasurement | undefined>,
  assemblyQcTestIdToRecord: Maybe<Guid>
) {
  const _allUsers = shallowRef<readonly User[]>([]);
  const _allVessels = shallowRef<readonly Vessel[]>([]);

  const vessels = makeDeepRef<Vessel[]>([]);
  const users = computed<readonly User[]>(() =>
    _allUsers.value.filter(userService.isNotPremierUser)
  );

  const { isLoaded: assemblyQcTestsIsLoaded, productionQcTests } =
    useAssemblyQcTests(
      assemblyId,
      ref(EnumAssemblyType.productPackaging) // this is used for addable tests and we do not care in this context.
    );

  // Map of measurement results; replaced wholesale on update (Vue 2/3 dual-compatible).
  const resultsMap = makeRef(new Map<string, ProductionQcMeasurementResult>());

  const notes = ref("");
  const vesselId = ref<Guid | null>(null);
  const userId = ref<Guid | null>(null);
  const measuredAt = ref<Instant>(Instant.now());
  const productionQcMeasurementHeaders = computed<
    ProductionQcMeasurementHeader[]
  >(() => {
    return productionQcTests.value.map<ProductionQcMeasurementHeader>(
      (test) => {
        return {
          assemblyQcTestId: test.assemblyQcTestId!,
          name: test.name,
          sequence: test.sequence,
          measurementLines: test.metrics.map<ProductionQcMeasurementLine>(
            (metric) => {
              const results = resultsMap.value.get(
                test.assemblyQcTestId!.toString() +
                  metric.definition.qcTestMetricLineId.toString()
              );

              return {
                assemblyQcTestMetricLineId:
                  metric.expectations.assemblyMetricLineId!,
                metricDefinition: metric.definition,
                value: results?.value ?? null,
                valueBoolean: results?.valueBoolean ?? null,
                valueString: results?.valueString ?? null,
                expectations: metric.expectations,
              };
            }
          ),
        };
      }
    );
  });

  const refreshAndCreateNewResultRecord = async () => {
    vessels.value = assemblyId.value
      ? _allVessels.value.filter(
          (vessel) =>
            vessel.readOnly.currentAssemblyId &&
            vessel.readOnly.currentAssemblyId.equals(assemblyId.value as Guid)
        )
      : [];

    if (assemblyQcMeasurementToEdit?.value) {
      resultsMap.value = new Map(
        assemblyQcMeasurementToEdit.value.lines.map((line) => [
          assemblyQcMeasurementToEdit.value!.header.assemblyQcTestId.toString() +
            line.assemblyQcTestMetricLineId.toString(),
          {
            value: line.value,
            valueBoolean: line.valueBoolean,
            valueString: line.valueString,
          },
        ])
      );
      notes.value = assemblyQcMeasurementToEdit.value.header.notes;
      vesselId.value = assemblyQcMeasurementToEdit.value.header.vesselId;
      userId.value = assemblyQcMeasurementToEdit.value.header.userId;
      measuredAt.value = assemblyQcMeasurementToEdit.value.header.measuredAt;
    } else {
      resultsMap.value = new Map();
      notes.value = "";
      vesselId.value = initialVesselId.value ?? vessels.value[0]?.id ?? null;
      userId.value = userStore.state.user?.id || null;
      measuredAt.value = Instant.now();
    }
  };

  const updateMeasurementValue = (
    assemblyQcTestId: Guid,
    qcTestMetricLineId: Guid,
    result: ProductionQcMeasurementResult
  ) => {
    const key = assemblyQcTestId.toString() + qcTestMetricLineId.toString();
    const next = new Map(resultsMap.value);
    next.set(key, result);
    resultsMap.value = next;
  };

  const saveMeasurements = withLoading(async () => {
    const testsWithCompleteMetricSetValues =
      productionQcMeasurementHeaders.value.filter((test) =>
        test.measurementLines.some((x) =>
          isNotNullOrUndefined(x.value ?? x.valueBoolean ?? x.valueString)
        )
      );

    const measurementPromises = [];

    for (const productionQcTestRecord of testsWithCompleteMetricSetValues) {
      let measurement = new AssemblyQcMeasurement();

      if (assemblyQcMeasurementToEdit?.value) {
        measurement = assemblyQcMeasurementToEdit?.value;
      } else {
        measurement.header.id = Guid.create();
      }

      measurement.header.notes = notes.value;
      measurement.header.vesselId = vesselId.value;
      measurement.header.measuredAt = measuredAt.value;
      measurement.header.userId =
        userId.value || userStore.state.user?.id || users.value[0].id;
      measurement.header.assemblyQcTestId =
        productionQcTestRecord.assemblyQcTestId;

      measurement.lines = productionQcTestRecord.measurementLines
        .filter((x) =>
          isNotNullOrUndefined(x.value ?? x.valueBoolean ?? x.valueString)
        )
        .map<AssemblyQcMeasurementLine>((measurement) => {
          const measurementLine = new AssemblyQcMeasurementLine();
          measurementLine.assemblyQcTestMetricLineId =
            measurement.assemblyQcTestMetricLineId;
          measurementLine.value = measurement.value;
          measurementLine.valueBoolean = measurement.valueBoolean;
          measurementLine.valueString = measurement.valueString;
          return measurementLine;
        });

      if (measurement.lines.length > 0) {
        measurementPromises.push(
          assemblyQcMeasurementService.saveAssemblyQcMeasurement(measurement)
        );
      }
    }
    await Promise.all(measurementPromises);
  });

  watch(
    productionQcTests,
    wrapPromiseWithErrorHandling(refreshAndCreateNewResultRecord),
    {
      immediate: true,
    }
  );

  onMounted(
    withLoading(async () => {
      [_allUsers.value, _allVessels.value] = await Promise.all([
        userService
          .getAllUsers(api.tenantGroupId)
          .then((a) => Object.freeze(a)),
        vesselService.getAllVessels().then((a) => Object.freeze(a)),
      ]);
    })
  );

  const filteredProductionQcMeasurementHeaders = computed(() => {
    return productionQcMeasurementHeaders.value
      .filter(
        (header) =>
          !assemblyQcTestIdToRecord ||
          header.assemblyQcTestId.equals(assemblyQcTestIdToRecord)
      )
      .filter(
        (header) =>
          assemblyQcMeasurementToEdit.value?.header.assemblyQcTestId.equals(
            header.assemblyQcTestId
          ) ?? true
      );
  });

  return {
    isLoaded: assemblyQcTestsIsLoaded,
    vessels,
    users,
    notes,
    vesselId,
    userId,
    measuredAt,
    filteredProductionQcMeasurementHeaders,
    updateMeasurementValue,
    saveMeasurements,
  };
}
